import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toEmbedUrl } from "@/lib/video";
import { isUploaded } from "@/lib/videoStream";
import LazyVideo from "@/components/LazyVideo";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Video, Search, VideoOff } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Classification from "@/components/Classification";

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/content/videos").then((r) => setVideos(r.data)).catch(() => {}); }, []);

  const results = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return videos;
    return videos.filter((v) => ["title", "product_family", "model", "sku", "problem", "description"]
      .some((f) => (v[f] || "").toString().toLowerCase().includes(t)));
  }, [q, videos]);

  return (
    <div className="space-y-6" data-testid="videos-page">
      <PageHeader overline="Media" title="Troubleshooting Videos" icon={Video}
        color="text-violet-600 bg-violet-50" desc="Approved video library. A video is only customer-facing when an Admin sets Customer Safe = Yes." />

      <div className="relative max-w-2xl">
        <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="video-search-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by product family, model, SKU or problem"
          className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-slate-200 bg-white text-base focus:outline-none focus:ring-2 focus:ring-violet-500" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {results.length === 0 && <p className="text-slate-400">No videos found.</p>}
        {results.map((v) => (
          <Card key={v.id} className="overflow-hidden flex flex-col" data-testid={`video-${v.id}`}>
            <div className="aspect-video bg-slate-900">
              {isUploaded(v) ? (
                <LazyVideo vid={v.id} testId={`video-player-${v.id}`} />
              ) : v.video_url ? (
                <iframe title={v.title} src={toEmbedUrl(v.video_url)} className="w-full h-full" allowFullScreen />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 gap-2">
                  <VideoOff className="w-8 h-8" />
                  <span className="text-xs">No video file — Admin to upload</span>
                </div>
              )}
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <div className="flex flex-wrap gap-2 mb-2">
                <Classification value={v.classification} />
                <Badge className={v.customer_safe === "Yes" ? "bg-emerald-500" : "bg-slate-500"}>
                  {v.customer_safe === "Yes" ? "Customer-safe" : "Not customer-facing"}
                </Badge>
                {v.status === "Draft" && <Badge variant="outline">Draft</Badge>}
              </div>
              <h3 className="font-heading font-bold text-slate-900 leading-tight">{v.title}</h3>
              <p className="text-sm text-slate-500 mt-1 flex-1">{v.description}</p>
              <div className="text-xs text-slate-400 mt-3 space-y-0.5">
                {v.product_family && <div>Family: {v.product_family}{v.model ? ` · ${v.model}` : ""}</div>}
                {v.problem && <div>Problem: {v.problem}</div>}
                {v.linked_articles && <div>Linked: {v.linked_articles}</div>}
                <div>Source: {v.source || "—"} · v{v.version} · reviewed {v.last_reviewed}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
